import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../../supabaseClient'
import { C, st, formatDate } from '../../theme'
import { Modal, PageHeader, EmptyState, PAGE_SIZE, SearchSelect } from '../../components/UI'
import { useAppPrefs } from '../../AppPrefs'
import { makeT } from '../../i18n'

// ── 階段設定（儲存於 localStorage，可自訂） ──────────────────────────────────
const STAGE_KEY = 'ypfops_production_stages'

const STAGE_COLORS = [
  '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444',
  '#10b981', '#ec4899', '#06b6d4', '#f97316',
  '#84cc16', '#6366f1',
]

// Module-level fallback for loadStages() — labels are raw, translated inside component
const _RAW_DEFAULT_STAGES = [
  { id: 'pending', label: '待開始', color: '#94a3b8', icon: '⏳', fixed: true },
  { id: 'stage_1', label: '生產中', color: '#3b82f6', icon: '⚙️',  fixed: false },
  { id: 'done',    label: '完成',   color: '#10b981', icon: '✅', fixed: true },
]

function loadStages() {
  try {
    const raw = localStorage.getItem(STAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed) && parsed.length >= 2) return parsed
    }
  } catch {}
  return _RAW_DEFAULT_STAGES
}
function saveStages(s) { localStorage.setItem(STAGE_KEY, JSON.stringify(s)) }

// Resolve a status value to stage object (handles legacy values gracefully)
function resolveStage(stages, id) {
  const found = stages.find(s => s.id === id)
  if (found) return found
  // Backward-compat: old hardcoded status values
  const legacy = {
    flat:           { label: '平板製作', color: '#3b82f6', icon: '📋' },
    box:            { label: '製箱',     color: '#8b5cf6', icon: '📦' },
    nailing:        { label: '打釘',     color: '#f59e0b', icon: '🔨' },
    in_progress:    { label: '生產中',   color: '#3b82f6', icon: '⚙️'  },
    post_production:{ label: '後製中',   color: '#8b5cf6', icon: '🔧' },
  }
  if (legacy[id]) return { id, ...legacy[id], fixed: false }
  return { id, label: id, color: '#94a3b8', icon: '●', fixed: false }
}

function getNextStage(stages, currentId) {
  const i = stages.findIndex(s => s.id === currentId)
  if (i >= 0 && i < stages.length - 1) return stages[i + 1]
  return stages[stages.length - 1]
}

const isOverdue = o =>
  o.due_date && new Date(o.due_date) < new Date() && o.status !== 'done'

// ── 流程管線視覺元件 ────────────────────────────────────────────────────────
function StagePipeline({ stages, currentId }) {
  const curIdx = stages.findIndex(s => s.id === currentId)
  const cur = resolveStage(stages, currentId)

  // Legacy / unknown status: show simple badge only
  if (curIdx < 0) {
    return (
      <span style={{
        fontSize: 11, fontWeight: 700, color: cur.color,
        background: cur.color + '18', padding: '3px 10px',
        borderRadius: 12, border: `1px solid ${cur.color}44`,
      }}>
        {cur.icon} {cur.label}
      </span>
    )
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', overflowX: 'auto', gap: 0 }}>
      {stages.map((stage, i) => {
        const done   = i < curIdx
        const active = i === curIdx
        return (
          <div key={stage.id} style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 4,
              padding: '4px 10px', borderRadius: 20,
              background: done ? '#f0fdf4' : active ? stage.color + '18' : '#f8fafc',
              border: `1.5px solid ${done ? '#a7f3d0' : active ? stage.color + '66' : C.border}`,
              fontSize: 11, fontWeight: active ? 700 : 500,
              color: done ? '#10b981' : active ? stage.color : '#94a3b8',
              transition: 'all 0.2s',
            }}>
              <span>{done ? '✓' : stage.icon}</span>
              <span>{stage.label}</span>
            </div>
            {i < stages.length - 1 && (
              <div style={{ width: 16, height: 2, background: done ? '#a7f3d0' : '#e2e8f0', margin: '0 2px', flexShrink: 0 }} />
            )}
          </div>
        )
      })}
    </div>
  )
}

// ── 階段設定 Modal ───────────────────────────────────────────────────────────
function StageSettingsModal({ stages, onSave, onClose, DEFAULT_STAGES, _ }) {
  const [local, setLocal] = useState(() => JSON.parse(JSON.stringify(stages)))
  const [newLabel, setNewLabel] = useState('')
  const [newIcon,  setNewIcon]  = useState('⚙️')
  const [newColor, setNewColor] = useState(STAGE_COLORS[0])

  const update = (id, field, val) =>
    setLocal(prev => prev.map(s => s.id === id ? { ...s, [field]: val } : s))

  const addStage = () => {
    if (!newLabel.trim()) return
    const id = 'stage_' + Date.now()
    const next = [...local]
    next.splice(next.length - 1, 0, { id, label: newLabel.trim(), color: newColor, icon: newIcon, fixed: false })
    setLocal(next)
    setNewLabel('')
    setNewIcon('⚙️')
    setNewColor(STAGE_COLORS[next.length % STAGE_COLORS.length])
  }

  const remove = id => setLocal(prev => prev.filter(s => s.id !== id))

  const move = (idx, dir) => {
    // dir: -1=up, 1=down; cannot cross index 0 (pending) or last (done)
    const next = [...local]
    const target = idx + dir
    if (target <= 0 || target >= next.length - 1) return
    ;[next[idx], next[target]] = [next[target], next[idx]]
    setLocal(next)
  }

  const handleSave = () => {
    if (local.length < 2) { alert('至少需要「待開始」和「完成」兩個階段'); return }
    onSave(local)
  }

  return (
    <Modal title={_('prodStageSettingsTitle')} onClose={onClose}>
      <p style={{ fontSize: 13, color: C.muted, marginBottom: 14 }}>
        自訂您的生產流程階段。首尾兩個階段（待開始、完成）固定不可刪除，中間階段可自由新增、改名、排序。
        設定會存在此裝置，所有使用者看到的是相同預設，管理員可在此更改。
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
        {local.map((stage, i) => (
          <div key={stage.id} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 12px', background: '#f8fafc', borderRadius: 10,
            border: `1.5px solid ${stage.fixed ? C.border : stage.color + '55'}`,
          }}>
            {/* Icon */}
            <input
              value={stage.icon}
              onChange={e => !stage.fixed && update(stage.id, 'icon', e.target.value)}
              readOnly={stage.fixed}
              maxLength={2}
              title="圖示（可用 emoji）"
              style={{
                width: 42, textAlign: 'center', fontSize: 18,
                border: `1px solid ${C.border}`, borderRadius: 8,
                padding: '4px', background: '#fff',
                cursor: stage.fixed ? 'default' : 'text',
              }}
            />
            {/* Label */}
            <input
              value={stage.label}
              onChange={e => update(stage.id, 'label', e.target.value)}
              placeholder="階段名稱"
              style={{ ...st.input, flex: 1, minWidth: 80 }}
            />
            {/* Color swatches (non-fixed only) */}
            {!stage.fixed && (
              <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                {STAGE_COLORS.slice(0, 6).map(c => (
                  <div key={c} onClick={() => update(stage.id, 'color', c)} style={{
                    width: 20, height: 20, borderRadius: '50%', background: c,
                    cursor: 'pointer',
                    boxSizing: 'border-box',
                    border: stage.color === c ? '3px solid #fff' : '2px solid transparent',
                    outline: stage.color === c ? `2px solid ${c}` : 'none',
                    transition: 'all 0.15s',
                  }} />
                ))}
              </div>
            )}
            {/* Move + delete (non-fixed only) */}
            {!stage.fixed ? (
              <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                <button style={{ ...st.btn('default'), padding: '4px 8px', fontSize: 11 }}
                  onClick={() => move(i, -1)} disabled={i <= 1} title="上移">↑</button>
                <button style={{ ...st.btn('default'), padding: '4px 8px', fontSize: 11 }}
                  onClick={() => move(i, 1)} disabled={i >= local.length - 2} title="下移">↓</button>
                <button style={{ ...st.btn('danger'), padding: '4px 8px', fontSize: 11 }}
                  onClick={() => remove(stage.id)} title="刪除此階段">✕</button>
              </div>
            ) : (
              <span style={{ fontSize: 10, color: C.muted, padding: '4px 8px', flexShrink: 0 }}>固定</span>
            )}
          </div>
        ))}
      </div>

      {/* Add new stage */}
      <div style={{ padding: '14px 16px', background: C.accentDim, borderRadius: 10, border: `1.5px dashed ${C.accent}`, marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.accent, marginBottom: 10 }}>＋ 新增階段（加在完成之前）</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            value={newIcon} onChange={e => setNewIcon(e.target.value)} maxLength={2}
            placeholder="🔧" title="圖示"
            style={{ width: 44, textAlign: 'center', fontSize: 18, border: `1px solid ${C.border}`, borderRadius: 8, padding: '6px', background: '#fff' }}
          />
          <input
            value={newLabel} onChange={e => setNewLabel(e.target.value)}
            placeholder="階段名稱（如：品質檢查、包裝）"
            style={{ ...st.input, flex: 1, minWidth: 120 }}
            onKeyDown={e => e.key === 'Enter' && addStage()}
          />
          <div style={{ display: 'flex', gap: 4 }}>
            {STAGE_COLORS.slice(0, 6).map(c => (
              <div key={c} onClick={() => setNewColor(c)} style={{
                width: 22, height: 22, borderRadius: '50%', background: c, cursor: 'pointer',
                boxSizing: 'border-box',
                border: newColor === c ? '3px solid #fff' : '2px solid transparent',
                outline: newColor === c ? `2px solid ${c}` : 'none',
              }} />
            ))}
          </div>
          <button style={{ ...st.btn(), padding: '8px 16px', fontSize: 12 }} onClick={addStage} disabled={!newLabel.trim()}>
            {_('addBtn')}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <button style={{ ...st.btn(), flex: 1, padding: '12px' }} onClick={handleSave}>{_('prodSaveSettings')}</button>
        <button style={{ ...st.btn('default'), padding: '12px 14px' }}
          onClick={() => { if (window.confirm('確定恢復預設階段設定？')) onSave(DEFAULT_STAGES) }}>
          {_('prodResetDefault')}
        </button>
        <button style={{ ...st.btn('default'), padding: '12px 14px' }} onClick={onClose}>{_('cancel')}</button>
      </div>
    </Modal>
  )
}

// ── 機台表單欄位 ────────────────────────────────────────────────────────────
const emptyMachineForm = () => ({
  name: '', code: '', department: '', location: '', responsible: '', supplier: '', status: 'active', note: '',
})

function MachineFormFields({ form, setForm, _ }) {
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }))
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
      {[
        [_('prodMachineName'), 'name', '例：CNC車床A'],
        [_('prodMachineCode'), 'code', '例：CNC-001'],
        [_('prodMachineDiv'),  'department', '例：生產部'],
        [_('prodMachineLocation'), 'location', '例：一廠二線'],
        [_('prodMachineResponsible'), 'responsible', '員工姓名'],
      ].map(([label, key, ph]) => (
        <div key={key}>
          <label style={st.label}>{label}</label>
          <input style={st.input} value={form[key] || ''} placeholder={ph} onChange={e => f(key, e.target.value)} />
        </div>
      ))}
      <div>
        <label style={st.label}>供應廠商</label>
        <input style={st.input} value={form.supplier || ''} placeholder="廠商名稱 / 聯絡方式" onChange={e => f('supplier', e.target.value)} />
      </div>
      <div>
        <label style={st.label}>狀態</label>
        <select style={st.input} value={form.status || 'active'} onChange={e => f('status', e.target.value)}>
          <option value="active">✅ {_('prodMachineStatusNormal')}</option>
          <option value="maintenance">⚙️ {_('prodMachineStatusMaint')}</option>
          <option value="inactive">⛔ {_('prodMachineStatusInactive')}</option>
        </select>
      </div>
      <div>
        <label style={st.label}>備注</label>
        <input style={st.input} value={form.note || ''} placeholder="其他說明" onChange={e => f('note', e.target.value)} />
      </div>
    </div>
  )
}

// ── 主模組 ──────────────────────────────────────────────────────────────────
export default function Production({ userProfile, showToast, addLog, onNavigateToWMS, isMobile, enabledModules = [] }) {
  const { lang } = useAppPrefs()
  const _ = makeT(lang)

  // Translated default stages (inside component so _() is available)
  const DEFAULT_STAGES = [
    { id: 'pending', label: _('prodStatusPending'), color: '#94a3b8', icon: '⏳', fixed: true },
    { id: 'stage_1', label: _('prodStatusInProd'),  color: '#3b82f6', icon: '⚙️',  fixed: false },
    { id: 'done',    label: _('prodStatusDone'),    color: '#10b981', icon: '✅', fixed: true },
  ]

  const isAdmin   = userProfile?.role === 'admin'
  const pos       = userProfile?.position || 'user'
  const hasProductionPerm = enabledModules.includes('production')
  const canManage = isAdmin || hasProductionPerm || [
    'factory_manager', 'senior_manager', 'section_chief',
    'team_leader', 'line_leader',
  ].includes(pos)
  const canDelete = isAdmin || hasProductionPerm || enabledModules.includes('delete_records') || ['factory_manager','senior_manager'].includes(pos)

  // ── Stage config ──
  const [stages, setStages]               = useState(() => loadStages())
  const [showStageSettings, setShowStageSettings] = useState(false)

  const handleSaveStages = newStages => {
    saveStages(newStages)
    setStages(newStages)
    setShowStageSettings(false)
    showToast('✓ 階段設定已儲存')
  }

  // ── Data ──
  const [orders,   setOrders]   = useState([])
  const [products, setProducts] = useState([])
  const [machines, setMachines] = useState([])
  const [loading,  setLoading]  = useState(true)
  const [saving,   setSaving]   = useState(false)

  // ── Page-level tab ──
  const [productionView, setProductionView] = useState('orders') // 'orders' | 'machines'

  // ── Machine management ──
  const [machineForm,    setMachineForm]    = useState(emptyMachineForm())
  const [editingMachine, setEditingMachine] = useState(null)
  const [showAddMachine, setShowAddMachine] = useState(false)

  // ── UI state ──
  const [filterStatus, setFilterStatus] = useState('all')
  const [search,       setSearch]       = useState('')
  const [page,         setPage]         = useState(1)
  const [selectedOrders, setSelectedOrders] = useState([])
  const [showDone,     setShowDone]     = useState(false)

  // ── Add work order ──
  const [showAdd,   setShowAdd]  = useState(false)
  const emptyOrder = () => ({
    order_no: '', product_id: '', product_name: '',
    quantity: '', start_date: '', due_date: '', note: '',
    machine_id: '', machine_name: '', machine_code: '', department: '',
  })
  const [newOrder, setNewOrder] = useState(emptyOrder())

  // ── Edit work order ──
  const [editingOrder, setEditingOrder] = useState(null)
  const [editForm,     setEditForm]     = useState({})

  const openEdit = order => {
    setEditForm({
      order_no:     order.order_no,
      product_id:   order.product_id || '',
      product_name: order.product_name || '',
      quantity:     String(order.quantity),
      start_date:   order.start_date || '',
      due_date:     order.due_date   || '',
      note:         order.note       || '',
      machine_id:   order.machine_id   || '',
      machine_name: order.machine_name || '',
      machine_code: order.machine_code || '',
      department:   order.department   || '',
    })
    setEditingOrder(order)
  }

  const handleSaveEdit = async () => {
    if (!editForm.order_no || !editForm.product_name || !editForm.quantity) {
      showToast('請填寫工單號、產品名稱和批次數量', 'error'); return
    }
    if (parseInt(editForm.quantity) <= 0) { showToast('數量必須大於 0', 'error'); return }
    setSaving(true)
    const { error } = await supabase.from('work_orders').update({
      order_no:     editForm.order_no,
      product_name: editForm.product_name,
      product_id:   editForm.product_id || null,
      quantity:     parseInt(editForm.quantity),
      start_date:   editForm.start_date || null,
      due_date:     editForm.due_date   || null,
      note:         editForm.note,
      machine_id:   editForm.machine_id   || null,
      machine_name: editForm.machine_name || null,
      machine_code: editForm.machine_code || null,
      department:   editForm.department   || null,
      updated_at:   new Date().toISOString(),
    }).eq('id', editingOrder.id)
    setSaving(false)
    if (error) { showToast('編輯失敗：' + error.message, 'error'); return }
    await addLog('編輯工單', `工單 ${editForm.order_no}：${editForm.product_name}`)
    showToast('✓ 工單已更新')
    setEditingOrder(null)
    load()
  }

  // ── Progress report ──
  const [reporting,   setReporting]   = useState(null)
  const [reportForm,  setReportForm]  = useState({
    note: '', reporter: '', finalQty: '', stockMode: 'manual', targetStageId: '',
    qc_user_name: '', qc_passed: null, qc_notes: '',
  })

  // ── History ──
  const [historyOrder, setHistoryOrder] = useState(null)
  const [historyLogs,  setHistoryLogs]  = useState([])

  // ── Load ──
  const load = async () => {
    setLoading(true)
    const cid = userProfile?.company_id
    let woQ = supabase.from('work_orders').select('*')
    if (cid) woQ = woQ.eq('company_id', cid)
    let pdQ = supabase.from('products').select('id, name, unit, stock')
    if (cid) pdQ = pdQ.eq('company_id', cid)
    let mcQ = supabase.from('machines').select('id, name, code, department, location, responsible, supplier, status, note')
    if (cid) mcQ = mcQ.eq('company_id', cid)
    const [{ data: od }, { data: pd }, { data: mc }] = await Promise.all([
      woQ.order('created_at', { ascending: false }).limit(500),
      pdQ.order('name').limit(500),
      mcQ.order('name').limit(200),
    ])
    if (od) setOrders(od)
    if (pd) setProducts(pd)
    if (mc) setMachines(mc)
    setLoading(false)
  }
  useEffect(() => { load() }, [])

  // Auto-generate order number
  useEffect(() => {
    if (showAdd && !newOrder.order_no) {
      const d = new Date()
      const prefix = `WO-${d.getFullYear()}${String(d.getMonth()+1).padStart(2,'0')}${String(d.getDate()).padStart(2,'0')}`
      supabase.from('work_orders').select('id', { count: 'exact', head: true }).like('order_no', `${prefix}%`)
        .then(({ count }) => {
          setNewOrder(o => ({ ...o, order_no: `${prefix}-${String((count||0)+1).padStart(3,'0')}` }))
        })
    }
  }, [showAdd])

  const productOptions = useMemo(() =>
    products.map(p => ({ value: p.id, label: p.name, sub: `庫存: ${p.stock} ${p.unit || '件'}` }))
  , [products])

  const machineOptions = useMemo(() =>
    machines.map(m => ({ value: m.id, label: `${m.code} · ${m.name}`, sub: `${m.department || ''}${m.location ? ' · ' + m.location : ''}`.trim() }))
  , [machines])

  const applyMachine = (id, setter) => {
    const m = machines.find(m => m.id === id)
    if (m) setter(f => ({ ...f, machine_id: id, machine_name: m.name, machine_code: m.code, department: f.department || m.department || '' }))
    else setter(f => ({ ...f, machine_id: id }))
  }

  // ── Stats (dynamic) ──
  const stats = useMemo(() => {
    const byStage = {}
    stages.forEach(s => { byStage[s.id] = 0 })
    orders.forEach(o => { if (byStage[o.status] !== undefined) byStage[o.status]++ })
    return {
      total: orders.length,
      byStage,
      overdue: orders.filter(o => isOverdue(o)).length,
    }
  }, [orders, stages])

  // ── Filter ──
  const filtered = useMemo(() => {
    if (filterStatus === 'overdue') return orders.filter(o => isOverdue(o))
    return orders.filter(o => {
      if (filterStatus !== 'all' && o.status !== filterStatus) return false
      if (search) {
        const q = search.toLowerCase()
        if (!o.order_no?.toLowerCase().includes(q) && !o.product_name?.toLowerCase().includes(q)) return false
      }
      return true
    })
  }, [orders, filterStatus, search])

  const paged      = useMemo(() => filtered.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE), [filtered, page])
  const totalPages = useMemo(() => Math.ceil(filtered.length / PAGE_SIZE), [filtered])

  // ── Add work order ──
  const handleAddOrder = async () => {
    if (!newOrder.order_no || !newOrder.product_name || !newOrder.quantity) {
      showToast('請填寫工單號、產品名稱和批次數量', 'error'); return
    }
    if (parseInt(newOrder.quantity) <= 0) { showToast('數量必須大於 0', 'error'); return }
    setSaving(true)
    const { error } = await supabase.from('work_orders').insert({
      order_no:    newOrder.order_no,
      product_name: newOrder.product_name,
      product_id:  newOrder.product_id || null,
      quantity:    parseInt(newOrder.quantity),
      completed_qty: 0,
      actual_qty:  0,
      status:      'pending',
      start_date:  newOrder.start_date || null,
      due_date:    newOrder.due_date   || null,
      note:        newOrder.note,
      operator:    userProfile?.name,
      stock_in_mode: 'manual',
      machine_id:  newOrder.machine_id || null,
      machine_name: newOrder.machine_name || null,
      machine_code: newOrder.machine_code || null,
      department:  newOrder.department || null,
      company_id:  userProfile?.company_id || null,
    })
    setSaving(false)
    if (error) {
      showToast(error.message.includes('unique') ? '工單號已存在，請換一個' : '建立失敗：' + error.message, 'error')
      return
    }
    await addLog('建立工單', `工單 ${newOrder.order_no}：${newOrder.product_name} × ${newOrder.quantity}`)
    showToast('✓ 工單已建立')
    setNewOrder(emptyOrder())
    setShowAdd(false)
    load()
  }

  // ── Open progress report ──
  const openReport = order => {
    const next = getNextStage(stages, order.status)
    setReporting(order)
    setReportForm({
      note: '', reporter: userProfile?.name || '',
      finalQty: String(order.quantity),
      stockMode: 'manual',
      targetStageId: next?.id || 'done',
      qc_user_name: '', qc_passed: null, qc_notes: '',
    })
  }

  // ── Submit progress report ──
  const handleReport = async () => {
    if (!reportForm.targetStageId) { showToast('請選擇要推進到的階段', 'error'); return }
    const targetId = reportForm.targetStageId
    const isDone   = targetId === 'done'

    if (isDone && (!reportForm.finalQty || parseInt(reportForm.finalQty) <= 0)) {
      showToast('完成時請填寫實際完成數量', 'error'); return
    }

    setSaving(true)
    const actualQty  = isDone ? parseInt(reportForm.finalQty) : (reporting.completed_qty || 0)
    const fromLabel  = resolveStage(stages, reporting.status).label
    const toLabel    = resolveStage(stages, targetId).label

    const qcUpdate = reportForm.qc_user_name ? {
      qc_user_name: reportForm.qc_user_name,
      qc_passed:    reportForm.qc_passed,
      qc_notes:     reportForm.qc_notes || null,
      qc_at:        new Date().toISOString(),
    } : {}

    await supabase.from('work_orders').update({
      status:       targetId,
      completed_qty: isDone ? actualQty : reporting.completed_qty,
      stock_in_mode: isDone ? reportForm.stockMode : 'manual',
      updated_at:   new Date().toISOString(),
      ...qcUpdate,
    }).eq('id', reporting.id)

    await supabase.from('production_logs').insert({
      work_order_id: reporting.id,
      qty:  actualQty,
      note: `${fromLabel} → ${toLabel}` +
            (reportForm.reporter ? ` · 回報人：${reportForm.reporter}` : '') +
            (reportForm.note     ? ` · ${reportForm.note}` : ''),
      operator: userProfile?.name,
      company_id: userProfile?.company_id || null,
    })

    if (isDone && reportForm.stockMode === 'auto' && reporting.product_id) {
      const product = products.find(p => p.id === reporting.product_id)
      if (product) {
        await Promise.all([
          supabase.from('products').update({ stock: product.stock + actualQty, updated_at: new Date().toISOString() }).eq('id', reporting.product_id),
          supabase.from('records').insert({ product_id: reporting.product_id, type: 'in', qty: actualQty, note: `生產完工自動入庫 · ${reporting.order_no}`, operator: userProfile?.name, company_id: userProfile?.company_id || null }),
          supabase.from('work_orders').update({ stock_in_mode: 'auto_done' }).eq('id', reporting.id),
        ])
        await addLog('生產完工入庫', `工單 ${reporting.order_no} 完工，自動入庫 ${actualQty} 件`)
        showToast(`✓ 完工！已自動入庫 ${actualQty} 件`)
      } else {
        await addLog('生產完工', `工單 ${reporting.order_no} 完工，找不到連結商品`)
        showToast('✓ 完工！找不到連結商品，請手動入庫')
      }
    } else if (isDone) {
      await addLog('生產完工', `工單 ${reporting.order_no} 完工，實際 ${actualQty} 件，等待手動入庫`)
      showToast(`✓ 工單完工！請至倉儲手動入庫 ${actualQty} 件`)
    } else {
      await addLog('生產進度', `工單 ${reporting.order_no}：${fromLabel} → ${toLabel}`)
      showToast(`✓ 已推進到：${toLabel}`)
    }

    setSaving(false)
    setReporting(null)
    load()
  }

  // ── History ──
  const openHistory = async order => {
    setHistoryOrder(order)
    setHistoryLogs([])
    const { data } = await supabase.from('production_logs').select('*')
      .eq('work_order_id', order.id).order('created_at', { ascending: false }).limit(50)
    setHistoryLogs(data || [])
  }

  // ── Delete ──
  const handleDelete = async (id, no) => {
    if (!window.confirm(`確定刪除工單 ${no}？`)) return
    await supabase.from('work_orders').delete().eq('id', id)
    await addLog('刪除工單', no)
    showToast('✓ 已刪除')
    setSelectedOrders(prev => prev.filter(s => s !== id))
    load()
  }
  const handleBatchDelete = async () => {
    if (!window.confirm(`確定刪除 ${selectedOrders.length} 筆工單？`)) return
    await supabase.from('work_orders').delete().in('id', selectedOrders)
    await addLog('批量刪除工單', `${selectedOrders.length} 筆`)
    showToast(`✓ 已刪除 ${selectedOrders.length} 筆`)
    setSelectedOrders([])
    load()
  }

  // ── Machine CRUD ──
  const handleSaveMachine = async () => {
    if (!machineForm.name.trim() || !machineForm.code.trim()) { showToast('請填寫機台名稱和代號', 'error'); return }
    setSaving(true)
    const { id, created_at, ...rest } = machineForm
    if (editingMachine) {
      const { error } = await supabase.from('machines').update(rest).eq('id', editingMachine.id)
      if (error) { showToast('更新失敗：' + error.message, 'error'); setSaving(false); return }
      showToast('✓ 機台已更新'); setEditingMachine(null); setMachineForm(emptyMachineForm())
    } else {
      const { error } = await supabase.from('machines').insert({ ...rest, company_id: userProfile?.company_id || null })
      if (error) { showToast('新增失敗：' + error.message, 'error'); setSaving(false); return }
      showToast('✓ 機台已新增'); setShowAddMachine(false); setMachineForm(emptyMachineForm())
    }
    setSaving(false); load()
  }

  const handleDeleteMachine = async (id, name) => {
    if (!window.confirm(`確定刪除機台「${name}」？\n注意：已關聯此機台的工單不會受影響。`)) return
    const { error } = await supabase.from('machines').delete().eq('id', id)
    if (error) { showToast('刪除失敗：' + error.message, 'error'); return }
    await addLog('刪除機台', `刪除機台 ${name}`)
    showToast('✓ 已刪除'); load()
  }

  // ── Filter tabs (dynamic) ──
  const filterTabs = useMemo(() => [
    { v: 'all', l: _('filterAll'), count: stats.total },
    ...stages.map(s => ({ v: s.id, l: `${s.icon} ${s.label}`, count: stats.byStage[s.id] || 0 })),
    ...(stats.overdue > 0 ? [{ v: 'overdue', l: _('prodFilterOverdue'), count: stats.overdue, warn: true }] : []),
  ], [stages, stats, lang])

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div>
      <PageHeader
        title="生產管理"
        en="Production Management"
        vi="Sản xuất"
        subtitle={stages.filter(s => !s.fixed).map(s => s.label).join(' · ') + ' · 工單追蹤'}
      />

      {/* Page-level Tab Switcher */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20, borderBottom: `2px solid ${C.border}`, paddingBottom: 0 }}>
        {[['orders', _('prodTabWorkOrders')], ['machines', _('prodTabMachines')]].map(([key, label]) => (
          <button key={key} onClick={() => setProductionView(key)}
            style={{ padding: '10px 18px', background: 'none', border: 'none', borderBottom: `3px solid ${productionView === key ? C.accent : 'transparent'}`, color: productionView === key ? C.accent : C.muted, fontSize: 14, fontWeight: productionView === key ? 800 : 500, cursor: 'pointer', marginBottom: -2, transition: 'all 0.15s' }}>
            {label}
            {key === 'machines' && machines.length > 0 && (
              <span style={{ marginLeft: 6, fontSize: 11, background: productionView === key ? C.accent : C.border, color: productionView === key ? '#fff' : C.muted, borderRadius: 20, padding: '1px 7px' }}>{machines.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* ══════ 工單管理 ══════ */}
      {productionView === 'orders' && <>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 10, marginBottom: 20 }}>
        <div style={{ background: C.accentDim, border: `2px solid ${C.accent}33`, borderRadius: 12, padding: '10px 12px' }}>
          <div style={{ fontSize: 18 }}>📋</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: C.accent }}>{stats.total}</div>
          <div style={{ fontSize: 11, color: C.sub, fontWeight: 600, marginTop: 2 }}>工單總數</div>
        </div>
        {stages.map(s => (
          <div key={s.id} style={{ background: s.color + '12', border: `2px solid ${s.color}33`, borderRadius: 12, padding: '10px 12px' }}>
            <div style={{ fontSize: 18 }}>{s.icon}</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: s.color }}>{stats.byStage[s.id] || 0}</div>
            <div style={{ fontSize: 11, color: C.sub, fontWeight: 600, marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
        {stats.overdue > 0 && (
          <div style={{ background: C.redBg, border: `2px solid ${C.red}`, borderRadius: 12, padding: '10px 12px' }}>
            <div style={{ fontSize: 18 }}>⚠️</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: C.red }}>{stats.overdue}</div>
            <div style={{ fontSize: 11, color: C.sub, fontWeight: 600, marginTop: 2 }}>逾期</div>
          </div>
        )}
      </div>

      {/* Filter tabs + Action buttons */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {filterTabs.map(({ v, l, count, warn }) => (
            <button key={v}
              style={{ ...st.btn(filterStatus === v ? (warn ? 'danger' : 'primary') : 'default'), padding: '7px 12px' }}
              onClick={() => { setFilterStatus(v); setPage(1); setSelectedOrders([]) }}>
              <span style={{ fontSize: 12 }}>{l}</span>
              {count > 0 && (
                <span style={{ fontSize: 10, opacity: 0.7, marginLeft: 4, background: filterStatus === v ? 'rgba(255,255,255,0.25)' : C.border, borderRadius: 10, padding: '1px 5px' }}>
                  {count}
                </span>
              )}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {isAdmin && selectedOrders.length > 0 && (
            <button style={{ ...st.btn('danger'), padding: '9px 14px' }} onClick={handleBatchDelete}>
              🗑 刪除({selectedOrders.length})
            </button>
          )}
          {isAdmin && (
            <button style={{ ...st.btn('default'), padding: '9px 14px', fontSize: 12 }}
              onClick={() => setShowStageSettings(true)} title="設定生產流程階段">
              {_('prodStageSettings')}
            </button>
          )}
          {canManage && (
            <button style={{ ...st.btn(), padding: '9px 16px' }}
              onClick={() => { setShowAdd(!showAdd); setNewOrder(emptyOrder()) }}>
              <span>{showAdd ? '✕ 取消' : _('prodNewOrder')}</span>
              <span style={{ fontSize: 9, opacity: 0.5, display: 'block' }}>{showAdd ? 'Cancel' : 'New Order'}</span>
            </button>
          )}
        </div>
      </div>

      {/* Search */}
      <div style={{ marginBottom: 16 }}>
        <input
          style={{ ...st.input, width: '100%', maxWidth: 340 }}
          value={search} placeholder={_('prodSearchOrders')}
          onChange={e => { setSearch(e.target.value); setPage(1) }}
        />
      </div>

      {/* Add form */}
      {showAdd && (
        <div style={{ ...st.card, marginBottom: 16, border: `2px solid ${C.accent}` }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 14 }}>🏭 建立生產工單 · New Work Order</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
            <div>
              <label style={st.label}>{_('prodOrderNo')}</label>
              <input style={st.input} value={newOrder.order_no} placeholder="自動產生"
                onChange={e => setNewOrder(o => ({ ...o, order_no: e.target.value }))} />
            </div>
            <div>
              <label style={st.label}>{_('prodLinkedProduct')}</label>
              <SearchSelect
                options={productOptions}
                value={newOrder.product_id}
                onChange={val => {
                  const p = products.find(p => p.id === val)
                  setNewOrder(o => ({ ...o, product_id: val, product_name: p?.name || '' }))
                }}
                placeholder="搜尋產品..."
              />
            </div>
            <div>
              <label style={st.label}>{_('prodOrderName')}</label>
              <input style={st.input} value={newOrder.product_name}
                placeholder="可手動填寫名稱"
                onChange={e => setNewOrder(o => ({ ...o, product_name: e.target.value }))} />
            </div>
            <div>
              <label style={st.label}>{_('prodBatchQty')}</label>
              <input type="number" min="1" style={st.input} value={newOrder.quantity}
                placeholder="0" onChange={e => setNewOrder(o => ({ ...o, quantity: e.target.value }))} />
            </div>
            <div>
              <label style={st.label}>{_('prodStartDate')}</label>
              <input type="date" style={st.input} value={newOrder.start_date}
                onChange={e => setNewOrder(o => ({ ...o, start_date: e.target.value }))} />
            </div>
            <div>
              <label style={st.label}>{_('prodDueDate')}</label>
              <input type="date" style={st.input} value={newOrder.due_date}
                onChange={e => setNewOrder(o => ({ ...o, due_date: e.target.value }))} />
            </div>
            <div>
              <label style={st.label}>{_('prodMachine')}</label>
              <SearchSelect
                options={machineOptions}
                value={newOrder.machine_id}
                onChange={val => applyMachine(val, setNewOrder)}
                placeholder={machines.length ? '搜尋機台代號/名稱...' : '（尚無機台，請至維修模組新增）'}
              />
            </div>
            <div>
              <label style={st.label}>{_('prodDepartment')}</label>
              <input style={st.input} value={newOrder.department} placeholder={_('prodDepartmentPh')}
                onChange={e => setNewOrder(o => ({ ...o, department: e.target.value }))} />
            </div>
            <div>
              <label style={st.label}>備注</label>
              <input style={st.input} value={newOrder.note} placeholder="說明..."
                onChange={e => setNewOrder(o => ({ ...o, note: e.target.value }))} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            <button style={{ ...st.btn(), flex: 1, padding: '12px' }} onClick={handleAddOrder} disabled={saving}>
              {saving ? _('savingBtn') : _('prodConfirmCreate')}
            </button>
            <button style={{ ...st.btn('default'), padding: '12px 18px' }} onClick={() => setShowAdd(false)}>{_('cancel')}</button>
          </div>
        </div>
      )}

      {/* Work order list */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: C.muted }}>{_('loading')}</div>
      ) : paged.length === 0 ? (
        <div style={st.card}>
          <EmptyState icon="🏭" text={search ? _('prodNoMatch') : _('prodNoOrders')} />
        </div>
      ) : (
        <>
          {isAdmin && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, fontSize: 12, color: C.muted }}>
              <input type="checkbox"
                checked={selectedOrders.length === paged.length && paged.length > 0}
                onChange={e => setSelectedOrders(e.target.checked ? paged.map(o => o.id) : [])}
                style={{ width: 15, height: 15 }} />
              <span>全選本頁</span>
              <span style={{ marginLeft: 'auto' }}>{filtered.length} 筆工單</span>
            </div>
          )}
          {/* Active orders */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {paged.filter(o => o.status !== 'done').map(o => {
              const overdue = isOverdue(o)
              const cur     = resolveStage(stages, o.status)
              const isDone  = false
              const isPending = o.status === 'pending'
              const unit    = products.find(p => p.id === o.product_id)?.unit || '件'
              const nextS   = getNextStage(stages, o.status)

              return (
                <div key={o.id} style={{
                  background: C.panel,
                  border: `2px solid ${overdue ? C.redBorder : cur.color + '44'}`,
                  borderRadius: 14, padding: '16px 18px',
                  transition: 'box-shadow 0.2s',
                }}>
                  {/* Header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {isAdmin && (
                        <input type="checkbox" checked={selectedOrders.includes(o.id)}
                          onChange={e => setSelectedOrders(prev =>
                            e.target.checked ? [...prev, o.id] : prev.filter(id => id !== o.id))}
                          style={{ width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }} />
                      )}
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 14, fontWeight: 800, color: C.accent, fontFamily: 'monospace' }}>{o.order_no}</span>
                          {overdue && (
                            <span style={{ fontSize: 11, fontWeight: 700, color: C.red, background: C.redBg, padding: '2px 8px', borderRadius: 10, border: `1px solid ${C.redBorder}` }}>
                              {_('prodBadgeOverdue')}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginTop: 2 }}>{o.product_name}</div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 22, fontWeight: 900, color: isDone ? '#10b981' : C.text }}>
                        {o.quantity}
                        <span style={{ fontSize: 12, fontWeight: 400, color: C.muted, marginLeft: 4 }}>{unit}</span>
                      </div>
                      {isDone && o.completed_qty > 0 && o.completed_qty !== o.quantity && (
                        <div style={{ fontSize: 11, color: C.muted }}>實際：{o.completed_qty} {unit}</div>
                      )}
                    </div>
                  </div>

                  {/* Pipeline */}
                  <div style={{ marginBottom: 12 }}>
                    <StagePipeline stages={stages} currentId={o.status} />
                  </div>

                  {/* Machine / Department tags */}
                  {(o.machine_name || o.machine_code || o.department) && (
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                      {(o.machine_name || o.machine_code) && (
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: C.accentDim, color: C.accent, border: `1px solid ${C.accent}33` }}>
                          ⚙️ {o.machine_code ? `${o.machine_code} · ` : ''}{o.machine_name}
                        </span>
                      )}
                      {o.department && (
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: '#f3e8ff', color: '#7c3aed', border: '1px solid #c4b5fd55' }}>
                          🏢 {o.department}
                        </span>
                      )}
                    </div>
                  )}

                  {/* QC info */}
                  {o.qc_user_name && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, padding: '6px 10px', background: o.qc_passed === true ? '#f0fdf4' : o.qc_passed === false ? '#fef2f2' : '#f8fafc', borderRadius: 8, border: `1px solid ${o.qc_passed === true ? '#a7f3d0' : o.qc_passed === false ? '#fca5a5' : '#e2e8f0'}`, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: o.qc_passed === true ? '#10b981' : o.qc_passed === false ? C.red : C.muted }}>
                        {o.qc_passed === true ? _('prodBadgeQcPassed') : o.qc_passed === false ? _('prodBadgeQcFailed') : '🔍 品管審核'}
                      </span>
                      <span style={{ fontSize: 11, color: C.muted }}>品管：{o.qc_user_name}</span>
                      {o.qc_notes && <span style={{ fontSize: 11, color: C.sub }}>— {o.qc_notes}</span>}
                    </div>
                  )}

                  {/* Meta */}
                  <div style={{ display: 'flex', gap: 16, fontSize: 12, color: C.muted, marginBottom: 12, flexWrap: 'wrap' }}>
                    {o.start_date && <span>📅 開始：{o.start_date}</span>}
                    {o.due_date   && <span style={{ color: overdue ? C.red : C.muted, fontWeight: overdue ? 700 : 400 }}>🎯 完成：{o.due_date}</span>}
                    {o.note       && <span>📝 {o.note}</span>}
                    <span>建立：{formatDate(o.created_at)}</span>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {canManage && !isDone && (
                      <button style={{ ...st.btn(isPending ? 'primary' : 'success'), padding: '8px 16px', fontSize: 13 }}
                        onClick={() => openReport(o)}>
                        {isPending ? _('prodBtnStart') : `${_('prodBtnAdvance')} ${nextS?.label || _('prodStatusDone')}`}
                      </button>
                    )}
                    {canManage && (
                      <button style={{ ...st.btn('default'), padding: '8px 14px', fontSize: 12 }}
                        onClick={() => openEdit(o)}>
                        {_('prodBtnEdit')}
                      </button>
                    )}
                    <button style={{ ...st.btn('default'), padding: '8px 14px', fontSize: 12 }}
                      onClick={() => openHistory(o)}>
                      {_('prodBtnHistory')}
                    </button>
                    {isDone && (() => {
                      const wmsEnabled = enabledModules.includes('wms')
                      return (
                        <button
                          style={{ ...st.btn('default'), padding: '8px 14px', fontSize: 12, opacity: wmsEnabled ? 1 : 0.55, cursor: wmsEnabled ? 'pointer' : 'not-allowed' }}
                          onClick={onNavigateToWMS}
                          title={wmsEnabled ? '' : '需開通倉儲管理模組'}>
                          {wmsEnabled ? _('prodBtnWarehouse') : '🔒 倉儲管理'}
                        </button>
                      )
                    })()}
                    {canDelete && (
                      <button style={{ ...st.btn('danger'), padding: '8px 12px', fontSize: 12, marginLeft: 'auto' }}
                        onClick={() => handleDelete(o.id, o.order_no)}>
                        刪除
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20 }}>
              <button disabled={page === 1} style={{ ...st.btn('default'), padding: '8px 14px' }}
                onClick={() => setPage(p => p-1)}>{_('prevPage')}</button>
              <span style={{ padding: '8px 14px', fontSize: 13, color: C.muted }}>{page} / {totalPages}</span>
              <button disabled={page === totalPages} style={{ ...st.btn('default'), padding: '8px 14px' }}
                onClick={() => setPage(p => p+1)}>{_('nextPage')}</button>
            </div>
          )}

          {/* Completed orders — collapsible compact list */}
          {(() => {
            const doneOrders = paged.filter(o => o.status === 'done')
            if (doneOrders.length === 0) return null
            return (
              <div style={{ marginTop: 8, border: `1.5px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
                <button
                  onClick={() => setShowDone(v => !v)}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', background: C.panelAlt, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700, color: C.muted }}
                >
                  <span>✅ {_('prodStatusDone')} ({doneOrders.length})</span>
                  <span style={{ fontSize: 11, transition: 'transform 0.2s', display: 'inline-block', transform: showDone ? 'rotate(180deg)' : 'none' }}>▼</span>
                </button>
                {showDone && (
                  <div>
                    {doneOrders.map((o, i) => {
                      const unit = products.find(p => p.id === o.product_id)?.unit || '件'
                      return (
                        <div key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 16px', borderTop: `1px solid ${C.border}`, background: C.panel, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: C.accent, fontFamily: 'monospace', flexShrink: 0 }}>{o.order_no}</span>
                          <span style={{ fontSize: 13, fontWeight: 600, color: C.text, flex: 1, minWidth: 80 }}>{o.product_name}</span>
                          <span style={{ fontSize: 12, color: C.muted, flexShrink: 0 }}>{o.completed_qty ?? o.quantity} {unit}</span>
                          {o.qc_passed === true && <span style={{ fontSize: 11, fontWeight: 700, color: '#10b981', background: '#f0fdf4', padding: '2px 8px', borderRadius: 10, border: '1px solid #a7f3d0', flexShrink: 0 }}>{_('prodBadgeQcPassed')}</span>}
                          {o.qc_passed === false && <span style={{ fontSize: 11, fontWeight: 700, color: C.red, background: C.redBg, padding: '2px 8px', borderRadius: 10, border: `1px solid ${C.redBorder}`, flexShrink: 0 }}>{_('prodBadgeQcFailed')}</span>}
                          {o.due_date && <span style={{ fontSize: 11, color: C.muted, flexShrink: 0 }}>🎯 {o.due_date}</span>}
                          <button style={{ ...st.btn('default'), padding: '4px 10px', fontSize: 11, minHeight: 'unset', flexShrink: 0 }} onClick={() => openHistory(o)}>{_('prodBtnHistory')}</button>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })()}
        </>
      )}

      {/* ── 回報進度 Modal ── */}
      {reporting && (() => {
        const curIdx = stages.findIndex(s => s.id === reporting.status)
        // Available targets = all stages after current position
        const availableTargets = stages.filter((_, i) => i > (curIdx >= 0 ? curIdx : 0))
        const unit = products.find(p => p.id === reporting.product_id)?.unit || '件'
        return (
          <Modal title={`${_('prodReportProgress')} — ${reporting.order_no}`} onClose={() => setReporting(null)}>
            <div style={{ padding: '12px 16px', background: '#f8fafc', borderRadius: 12, marginBottom: 18 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 6 }}>{reporting.product_name}</div>
              <div style={{ display: 'flex', gap: 16, fontSize: 13, color: C.muted, flexWrap: 'wrap', marginBottom: 10 }}>
                <span>批次：<strong style={{ color: C.text }}>{reporting.quantity} {unit}</strong></span>
                {reporting.due_date && (
                  <span>預計完成：<strong style={{ color: isOverdue(reporting) ? C.red : C.text }}>{reporting.due_date}</strong></span>
                )}
              </div>
              <StagePipeline stages={stages} currentId={reporting.status} />
            </div>

            {/* Target stage */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ ...st.label, marginBottom: 8, display: 'block' }}>推進到 · Move To *</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
                {availableTargets.map((stage, i) => (
                  <button key={stage.id}
                    onClick={() => setReportForm(f => ({ ...f, targetStageId: stage.id }))}
                    style={{
                      padding: '12px', borderRadius: 10, cursor: 'pointer',
                      background: reportForm.targetStageId === stage.id ? stage.color + '18' : '#f8fafc',
                      border: `2px solid ${reportForm.targetStageId === stage.id ? stage.color + '88' : C.border}`,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                      transition: 'all 0.15s',
                    }}>
                    <span style={{ fontSize: 22 }}>{stage.icon}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: reportForm.targetStageId === stage.id ? stage.color : C.text }}>
                      {stage.label}
                    </span>
                    {i === 0 && <span style={{ fontSize: 9, color: stage.color, fontWeight: 600 }}>下一階段</span>}
                  </button>
                ))}
              </div>
            </div>

            {/* Completion info (only when targeting 'done') */}
            {reportForm.targetStageId === 'done' && (
              <div style={{ marginBottom: 14, padding: '14px 16px', background: '#f0fdf4', borderRadius: 10, border: '1.5px solid #a7f3d0' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#10b981', marginBottom: 10 }}>{_('prodCompletionInfo')}</div>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={st.label}>{_('prodActualQty')}</label>
                    <input type="number" min="1" style={st.input}
                      value={reportForm.finalQty} placeholder={reporting.quantity}
                      onChange={e => setReportForm(f => ({ ...f, finalQty: e.target.value }))} />
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>計劃：{reporting.quantity} {unit}</div>
                  </div>
                  <div>
                    <label style={st.label}>{_('prodStockMode')}</label>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {[['auto', _('prodAutoStock')], ['manual', _('prodManualStock')]].map(([v, l]) => (
                        <button key={v} onClick={() => setReportForm(f => ({ ...f, stockMode: v }))}
                          style={{ ...st.btn(reportForm.stockMode === v ? (v === 'auto' ? 'success' : 'primary') : 'default'), flex: 1, padding: '10px 6px', fontSize: 11 }}>
                          {l}
                        </button>
                      ))}
                    </div>
                    {reportForm.stockMode === 'auto' && !reporting.product_id && (
                      <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 6 }}>⚠ 未連結商品，將改為手動入庫</div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Reporter + note */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div>
                <label style={st.label}>回報人員</label>
                <input style={st.input} value={reportForm.reporter} placeholder="姓名或部門"
                  onChange={e => setReportForm(f => ({ ...f, reporter: e.target.value }))} />
              </div>
              <div>
                <label style={st.label}>備注</label>
                <input style={st.input} value={reportForm.note} placeholder="說明、問題..."
                  onChange={e => setReportForm(f => ({ ...f, note: e.target.value }))} />
              </div>
            </div>

            {/* QC Section */}
            <div style={{ marginBottom: 16, padding: '14px 16px', background: '#fafafa', borderRadius: 10, border: `1.5px solid ${reportForm.qc_passed === true ? '#a7f3d0' : reportForm.qc_passed === false ? '#fca5a5' : C.border}` }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.sub, marginBottom: 10 }}>{_('prodQcRecord')}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
                <div>
                  <label style={st.label}>{_('prodQcName')}</label>
                  <input style={st.input} value={reportForm.qc_user_name}
                    placeholder="品管人員姓名"
                    onChange={e => setReportForm(f => ({ ...f, qc_user_name: e.target.value }))} />
                </div>
                <div>
                  <label style={st.label}>{_('prodQcResult')}</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {[[true, _('prodQcPass'), 'success'], [false, _('prodQcFail'), 'danger'], [null, _('prodQcPending'), 'default']].map(([v, l, t]) => (
                      <button key={String(v)} onClick={() => setReportForm(f => ({ ...f, qc_passed: v }))}
                        style={{ ...st.btn(reportForm.qc_passed === v ? t : 'default'), flex: 1, padding: '8px 4px', fontSize: 11, minHeight: 36 }}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={st.label}>品管備注</label>
                  <input style={st.input} value={reportForm.qc_notes}
                    placeholder="問題描述、改善建議..."
                    onChange={e => setReportForm(f => ({ ...f, qc_notes: e.target.value }))} />
                </div>
              </div>
            </div>

            <button
              style={{ ...st.btn(reportForm.targetStageId === 'done' ? 'success' : ''), width: '100%', padding: '14px', fontSize: 14 }}
              onClick={handleReport}
              disabled={saving || !reportForm.targetStageId}>
              {saving ? _('processingBtn')
                : reportForm.targetStageId
                  ? `確認推進至：${resolveStage(stages, reportForm.targetStageId).label}`
                  : '請選擇目標階段'}
            </button>
          </Modal>
        )
      })()}

      {/* ── 工單歷史 Modal ── */}
      {historyOrder && (
        <Modal title={`${_('prodHistoryTitle')} — ${historyOrder.order_no}`} onClose={() => setHistoryOrder(null)}>
          <div style={{ padding: '12px 16px', background: '#f8fafc', borderRadius: 10, marginBottom: 16 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 4 }}>{historyOrder.product_name}</div>
            <div style={{ display: 'flex', gap: 12, fontSize: 13, color: C.muted, flexWrap: 'wrap' }}>
              <span>批次：<strong style={{ color: C.text }}>{historyOrder.quantity}</strong></span>
              {historyOrder.completed_qty > 0 && (
                <span>實際完成：<strong style={{ color: '#10b981' }}>{historyOrder.completed_qty}</strong></span>
              )}
              <span>狀態：<strong style={{ color: resolveStage(stages, historyOrder.status).color }}>
                {resolveStage(stages, historyOrder.status).label}
              </strong></span>
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <StagePipeline stages={stages} currentId={historyOrder.status} />
          </div>
          {historyLogs.length === 0 ? (
            <EmptyState icon="📋" text={_('prodNoHistory')} />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {historyLogs.map((log, i) => (
                <div key={log.id} style={{
                  padding: '10px 14px',
                  background: i === 0 ? C.accentDim : '#f8fafc',
                  borderRadius: 10,
                  border: `1.5px solid ${i === 0 ? C.accent + '44' : C.border}`,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ fontSize: 13, color: C.text, fontWeight: i === 0 ? 700 : 400, flex: 1 }}>{log.note}</div>
                    <div style={{ fontSize: 11, color: C.muted, flexShrink: 0 }}>{formatDate(log.created_at)}</div>
                  </div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>by {log.operator}</div>
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}

      {/* ── 編輯工單 Modal ── */}
      {editingOrder && (
        <Modal title={`${_('prodEditTitle')} — ${editingOrder.order_no}`} onClose={() => setEditingOrder(null)}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
            <div>
              <label style={st.label}>{_('prodOrderNo')}</label>
              <input style={st.input} value={editForm.order_no}
                onChange={e => setEditForm(f => ({ ...f, order_no: e.target.value }))} />
            </div>
            <div>
              <label style={st.label}>{_('prodLinkedProduct')}</label>
              <SearchSelect
                options={productOptions}
                value={editForm.product_id}
                onChange={val => {
                  const p = products.find(p => p.id === val)
                  setEditForm(f => ({ ...f, product_id: val, product_name: p?.name || f.product_name }))
                }}
                placeholder="搜尋產品..."
              />
            </div>
            <div>
              <label style={st.label}>{_('prodOrderName')}</label>
              <input style={st.input} value={editForm.product_name}
                onChange={e => setEditForm(f => ({ ...f, product_name: e.target.value }))} />
            </div>
            <div>
              <label style={st.label}>{_('prodBatchQty')}</label>
              <input type="number" min="1" style={st.input} value={editForm.quantity}
                onChange={e => setEditForm(f => ({ ...f, quantity: e.target.value }))} />
            </div>
            <div>
              <label style={st.label}>{_('prodStartDate')}</label>
              <input type="date" style={st.input} value={editForm.start_date}
                onChange={e => setEditForm(f => ({ ...f, start_date: e.target.value }))} />
            </div>
            <div>
              <label style={st.label}>{_('prodDueDate')}</label>
              <input type="date" style={st.input} value={editForm.due_date}
                onChange={e => setEditForm(f => ({ ...f, due_date: e.target.value }))} />
            </div>
            <div>
              <label style={st.label}>{_('prodMachine')}</label>
              <SearchSelect
                options={machineOptions}
                value={editForm.machine_id}
                onChange={val => applyMachine(val, setEditForm)}
                placeholder={machines.length ? '搜尋機台...' : '（尚無機台）'}
              />
            </div>
            <div>
              <label style={st.label}>{_('prodDepartment')}</label>
              <input style={st.input} value={editForm.department} placeholder={_('prodDepartmentPh')}
                onChange={e => setEditForm(f => ({ ...f, department: e.target.value }))} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={st.label}>備注</label>
              <input style={st.input} value={editForm.note} placeholder="說明..."
                onChange={e => setEditForm(f => ({ ...f, note: e.target.value }))} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button style={{ ...st.btn(), flex: 1, padding: '12px' }} onClick={handleSaveEdit} disabled={saving}>
              {saving ? _('savingBtn') : _('prodSaveChanges')}
            </button>
            <button style={{ ...st.btn('default'), padding: '12px 18px' }} onClick={() => setEditingOrder(null)}>{_('cancel')}</button>
          </div>
        </Modal>
      )}

      {/* ── 階段設定 Modal ── */}
      {showStageSettings && (
        <StageSettingsModal
          stages={stages}
          onSave={handleSaveStages}
          onClose={() => setShowStageSettings(false)}
          DEFAULT_STAGES={DEFAULT_STAGES}
          _={_}
        />
      )}

      </> /* end productionView === 'orders' */}

      {/* ══════ 機台管理 ══════ */}
      {productionView === 'machines' && (
        <div>
          {/* 操作列 */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
            <div style={{ fontSize: 13, color: C.muted }}>共 {machines.length} 台機器 · 資料與維修模組共用</div>
            {canManage && (
              <button style={{ ...st.btn(), padding: '9px 16px' }}
                onClick={() => { setShowAddMachine(!showAddMachine); setMachineForm(emptyMachineForm()); setEditingMachine(null) }}>
                <span>{showAddMachine ? '✕ 取消' : _('prodAddMachine')}</span>
                <span style={{ fontSize: 9, opacity: 0.5, display: 'block' }}>Add Machine</span>
              </button>
            )}
          </div>

          {/* 新增表單 */}
          {showAddMachine && !editingMachine && (
            <div style={{ ...st.card, marginBottom: 16, border: `2px solid ${C.accent}` }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 14 }}>{_('prodAddMachineTitle')}</div>
              <MachineFormFields form={machineForm} setForm={setMachineForm} _={_} />
              <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                <button style={{ ...st.btn(), flex: 1, padding: '12px' }} onClick={handleSaveMachine} disabled={saving}>
                  {saving ? _('savingBtn') : '確認新增'}
                </button>
                <button style={{ ...st.btn('default'), padding: '12px 18px' }} onClick={() => setShowAddMachine(false)}>{_('cancel')}</button>
              </div>
            </div>
          )}

          {/* 機台卡片列表 */}
          {machines.length === 0 ? (
            <div style={st.card}><EmptyState icon="⚙️" text={_('prodNoMachines')} /></div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
              {machines.map(m => {
                const sMap = {
                  active:      { label: _('prodMachineStatusNormal'), color: '#10b981', bg: '#d1fae5' },
                  maintenance: { label: _('prodMachineStatusMaint'),  color: '#f59e0b', bg: '#fffbeb' },
                  inactive:    { label: _('prodMachineStatusInactive'), color: '#94a3b8', bg: '#f1f5f9' },
                }
                const ms = sMap[m.status] || sMap.active
                const usedCount = orders.filter(o => o.machine_id === m.id).length
                const activeCount = orders.filter(o => o.machine_id === m.id && o.status !== 'done').length
                return (
                  <div key={m.id} style={{ background: '#fff', borderRadius: 14, border: `1.5px solid ${activeCount > 0 ? C.accent + '66' : C.border}`, padding: '16px 18px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                      <div>
                        <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>{m.name}</div>
                        <div style={{ fontSize: 11, fontFamily: 'monospace', color: C.accent, marginTop: 2 }}>{m.code}</div>
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, color: ms.color, background: ms.bg, padding: '3px 10px', borderRadius: 20 }}>{ms.label}</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 }}>
                      {m.department && <div style={{ fontSize: 12, color: C.muted }}>🏭 {m.department}{m.location ? ` · ${m.location}` : ''}</div>}
                      {m.responsible && <div style={{ fontSize: 12, color: C.muted }}>👤 {m.responsible}</div>}
                      {m.supplier && <div style={{ fontSize: 12, color: C.muted }}>🏪 {m.supplier}</div>}
                      {m.note && <div style={{ fontSize: 11, color: C.muted, fontStyle: 'italic' }}>📝 {m.note}</div>}
                    </div>
                    {/* 生產工單統計 */}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                      <div style={{ flex: 1, textAlign: 'center', background: activeCount > 0 ? C.accentDim : '#f8fafc', borderRadius: 8, padding: '6px' }}>
                        <div style={{ fontSize: 18, fontWeight: 900, color: activeCount > 0 ? C.accent : C.muted }}>{activeCount}</div>
                        <div style={{ fontSize: 9, color: C.muted }}>進行中工單</div>
                      </div>
                      <div style={{ flex: 1, textAlign: 'center', background: '#f8fafc', borderRadius: 8, padding: '6px' }}>
                        <div style={{ fontSize: 18, fontWeight: 900, color: C.muted }}>{usedCount}</div>
                        <div style={{ fontSize: 9, color: C.muted }}>累計工單</div>
                      </div>
                    </div>
                    {canManage && (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button style={{ ...st.btn('warning'), flex: 1, padding: '7px' }}
                          onClick={() => { setEditingMachine(m); setMachineForm({ ...m }); setShowAddMachine(false) }}>
                          編輯
                        </button>
                        <button style={{ ...st.btn('danger'), padding: '7px 10px' }}
                          onClick={() => handleDeleteMachine(m.id, m.name)}>
                          刪除
                        </button>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {/* 編輯機台 Modal */}
          {editingMachine && (
            <Modal title={`${_('prodEditMachineTitle')} — ${editingMachine.name}`} onClose={() => { setEditingMachine(null); setMachineForm(emptyMachineForm()) }}>
              <MachineFormFields form={machineForm} setForm={setMachineForm} _={_} />
              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <button style={{ ...st.btn(), flex: 1, padding: '12px' }} onClick={handleSaveMachine} disabled={saving}>
                  {saving ? _('savingBtn') : _('prodSaveChanges')}
                </button>
                <button style={{ ...st.btn('default'), padding: '12px 18px' }} onClick={() => { setEditingMachine(null); setMachineForm(emptyMachineForm()) }}>{_('cancel')}</button>
              </div>
            </Modal>
          )}
        </div>
      )}
    </div>
  )
}
